// Keep an uncertain request's identity until it succeeds. Never retry a write
// automatically: the operator can retry the same action after a timeout.
export const inventoryPath = url => /^\/?(?:action-center\/proposals|proposals\/[^/]+\/approve|master-reconciliation\/recipe-proposals|recipes(?:\/|\?|$)|inventory-cost|finance|daily-reports(?:\?|$)|purchasing|storage-setup|sales-consumption|bills\/[^/]+\/complete(?:\?|$)|imports\/[^/]+\/(?:commit|recipes\/[^/]+)(?:\?|$)|prep-items(?:\?|$)|rotational-counts|ingredients(?:\/|\?|$)|storage-locations(?:\/|\?|$)|departments(?:\/|\?|$)|ingredient-stock|inventory\/attention|operating-inventory\/items|purchase-queue\/items|purchases(?:\?|$)|prep-stock|production-jobs|production-storage-lots|production-tasks|prep-items\/[^/]+(?:\?|$|\/(?:start-production|complete-production|plan-production|count-context)))/.test(url || "");
const operationHeader = "X-Inventory-Operation-Key";
// Match inventory_atomic.request_identity. Existing recovery keys (for example
// cost-<uuid>) are also valid: replacing those could duplicate a committed write.
export const validOperationKey = value => typeof value === "string" && /^[A-Za-z0-9:_-]{8,120}$/.test(value);
const suppliedOperationKey = headers => typeof headers.get === "function"
  ? headers.get(operationHeader)
  : headers[Object.keys(headers).find(name => name.toLowerCase() === operationHeader.toLowerCase())];
const setOperationKey = (headers, value) => {
  if (typeof headers.set === "function") headers.set(operationHeader, value, true);
  else {
    Object.keys(headers).filter(name => name.toLowerCase() === operationHeader.toLowerCase()).forEach(name => delete headers[name]);
    headers[operationHeader] = value;
  }
};
export async function beginConsumableCount(api, itemId) {
  const context=(await api.post(`/operating-inventory/items/${itemId}/count-context`))?.data;
  if(context?.count_context_id){const entries=countContexts.get(api)||new Map();entries.set(`consumable:${itemId}`,context.count_context_id);countContexts.set(api,entries);}
  return context;
}
const countContexts = new WeakMap();
export async function beginPrepCount(api, itemId) {
  const response = await api.post(`/prep-items/${itemId}/count-context`);
  const context = response?.data;
  if (context?.count_context_id) {
    const entries = countContexts.get(api) || new Map();
    entries.set(`item:${itemId}`, context.count_context_id);
    (context.batch_ids || []).forEach(id => entries.set(`batch:${id}`, context.count_context_id));
    countContexts.set(api, entries);
  }
  return context;
}

export function installInventoryRetries(api) {
  const storageKey = "murai.inventory.pending.v1";
  let saved;
  try { saved = JSON.parse(window.sessionStorage.getItem(storageKey) || "[]"); } catch (_) { saved = []; }
  const entries = Array.isArray(saved) ? saved : [];
  const usableEntries = entries.filter(entry => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === "string" && validOperationKey(entry[1]));
  const pending = new Map(usableEntries);
  let storageNeedsRepair = usableEntries.length !== entries.length;
  const persist = () => { window.sessionStorage.setItem(storageKey, JSON.stringify([...pending])); };
  api.interceptors.request?.use(async config => {
    if (!["post", "patch", "put", "delete"].includes(String(config.method).toLowerCase()) || !inventoryPath(config.url)) return config;
    const batch = /prep-stock-batches\/([^/]+)\/adjust/.exec(config.url || "");
    const consumable = /operating-inventory\/items\/([^/]+)\/events$/.exec(config.url||'');
    const context = consumable&&config.data?.event_type==='count'?countContexts.get(api)?.get(`consumable:${consumable[1]}`):batch ? countContexts.get(api)?.get(`batch:${batch[1]}`)
      : /prep-stock-checks\/[^/]+\/lines/.test(config.url || "") ? countContexts.get(api)?.get(`item:${config.data?.prep_item_id}`) : null;
    config.headers = config.headers || {};
    if (context) config.headers["X-Inventory-Count-Context"] = context;
    const bytes = new TextEncoder().encode(JSON.stringify([String(config.method).toLowerCase(), config.url, config.data ?? null,config.headers['X-Inventory-Count-Context']||null]));
    const signature = [...new Uint8Array(await window.crypto.subtle.digest("SHA-256", bytes))].map(x => x.toString(16).padStart(2, "0")).join("");
    const supplied = suppliedOperationKey(config.headers);
    const stored = pending.get(signature);
    const identity = validOperationKey(supplied) ? supplied : validOperationKey(stored) ? stored : window.crypto.randomUUID();
    // Persist the key actually sent, including explicit recovery keys, so an
    // uncertain request keeps its identity after remount/reload as well as retry.
    if (pending.get(signature) !== identity || storageNeedsRepair) {
      pending.set(signature, identity); persist(); storageNeedsRepair = false;
    }
    setOperationKey(config.headers, identity);
    config.inventorySignature = signature;
    return config;
  });
  api.interceptors.response.use(response => {
    if (response.config?.inventorySignature) { pending.delete(response.config.inventorySignature); persist(); }
    return response;
  }, error => {
    // A definitive client rejection cannot have committed this operation.
    if (error.response?.status >= 400 && error.response?.status < 500 && error.config?.inventorySignature) { pending.delete(error.config.inventorySignature); persist(); }
    return Promise.reject(error);
  });
}
